import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  axiosInstance, fmt, getApiErrorMessage, inputCls,
  Field, Modal, ROLE_META, RoleBadge,
} from './_shared';

export default function UsersTab({ isSuperAdmin }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '' });
  const [resetPwUser, setResetPwUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [search, setSearch] = useState('');
  const [deletingUser, setDeletingUser] = useState(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const TOGGLEABLE = ['player', 'creator', 'team_owner', 'sponsor', 'admin'];

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axiosInstance.get('/admin/users'); setUsers(r.data); }
    catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleRole = async (userId, role, active) => {
    try {
      await axiosInstance.patch(`/admin/users/${userId}/roles`, { role, active });
      toast.success(`Role ${active ? 'added' : 'removed'}`);
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
  };

  const saveEdit = async () => {
    try {
      await axiosInstance.patch(`/admin/users/${editUser.id}`, editForm);
      toast.success('User updated');
      setEditUser(null);
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
  };

  const resetPassword = async () => {
    if (!newPassword || newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    try {
      await axiosInstance.patch(`/admin/users/${resetPwUser.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${resetPwUser.email}`);
      setResetPwUser(null);
      setNewPassword('');
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to reset password')); }
  };

  const deleteUser = async (userId) => {
    try {
      await axiosInstance.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      setDeletingUser(null);
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to delete user')); }
  };

  const handleBulkImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setBulkImporting(true);
    setBulkResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axiosInstance.post('/admin/users/bulk-import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBulkResult(res.data);
      toast.success(`${res.data.summary.created} users created`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import users');
    } finally {
      setBulkImporting(false);
      e.target.value = '';
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await axiosInstance.get('/admin/users/bulk-import/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bulk_import_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E54562]" /></div>;

  return (
    <div className="space-y-4">
      {/* Delete Confirmation Modal */}
      {deletingUser && (
        <Modal title="Delete User" onClose={() => setDeletingUser(null)}>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete <strong>{deletingUser.name}</strong>?
            </p>
            <p className="text-xs text-red-500">
              This will permanently delete the user and all their registrations and bookings.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setDeletingUser(null)} className="flex-1 py-3 bg-gray-200 text-gray-700 font-bold rounded-2xl">
                Cancel
              </button>
              <button onClick={() => deleteUser(deletingUser.id)} className="flex-1 py-3 bg-red-500 text-white font-extrabold rounded-2xl">
                Delete User
              </button>
            </div>
          </div>
        </Modal>
      )}

      {editUser && (
        <Modal title="Edit User" onClose={() => setEditUser(null)}>
          <div className="space-y-4">
            <Field label="Name" required><input className={inputCls} value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} /></Field>
            <Field label="Email" required><input className={inputCls} value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} /></Field>
            <button onClick={saveEdit} className="w-full py-3 bg-[#2B3A67] text-white font-extrabold rounded-2xl">Save Changes</button>
          </div>
        </Modal>
      )}

      {resetPwUser && (
        <Modal title="Reset Password" onClose={() => setResetPwUser(null)}>
          <div className="space-y-4">
            <p className="text-sm text-gray-500">Reset password for <span className="font-bold">{resetPwUser.name}</span> ({resetPwUser.email})</p>
            <Field label="New Password" required>
              <input className={inputCls} type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 6 characters" data-testid="new-password-input" />
            </Field>
            <button onClick={resetPassword} className="w-full py-3 bg-amber-500 text-white font-extrabold rounded-2xl" data-testid="reset-pw-submit">Reset Password</button>
          </div>
        </Modal>
      )}

      <div className="flex items-center gap-3">
        <input className={inputCls + ' flex-1'} placeholder="Search by name or email…" value={search} onChange={e => setSearch(e.target.value)} />
        {isSuperAdmin && (
          <button
            onClick={() => { setShowBulkImport(true); setBulkResult(null); }}
            className="px-4 py-2.5 bg-[#2B3A67] text-white font-bold rounded-xl text-sm hover:bg-[#1e2a4a] flex items-center gap-2 whitespace-nowrap"
            data-testid="bulk-import-btn"
          >
            <i className="ph-bold ph-upload-simple"></i>
            Bulk Import
          </button>
        )}
      </div>
      <p className="text-xs text-gray-400">{filtered.length} users</p>

      {/* Bulk Import Modal */}
      {showBulkImport && (
        <Modal title="Bulk Import Users" onClose={() => setShowBulkImport(false)}>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Upload a CSV file with columns: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">name, email, mobile, password, role</code>
            </p>
            <p className="text-xs text-gray-400">
              Role is optional (defaults to "user"). Valid roles: user, admin, player, team_owner, sponsor.
            </p>

            <div className="flex gap-3">
              <button
                onClick={downloadTemplate}
                className="flex-1 py-2.5 border border-[#2B3A67] text-[#2B3A67] rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#2B3A67]/5"
                data-testid="download-template-btn"
              >
                <i className="ph-bold ph-download-simple"></i>
                Download Template
              </button>
              <label className="flex-1 py-2.5 bg-[#3AAFA9] text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 cursor-pointer hover:bg-[#2D8A85]">
                <i className="ph-bold ph-file-csv"></i>
                {bulkImporting ? 'Importing...' : 'Upload CSV'}
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleBulkImport}
                  disabled={bulkImporting}
                  className="hidden"
                  data-testid="bulk-import-file-input"
                />
              </label>
            </div>

            {/* Import Results */}
            {bulkResult && (
              <div className="space-y-3 pt-3 border-t border-gray-100">
                <h4 className="font-bold text-sm text-[#2B3A67]">Import Results</h4>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-green-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-black text-green-600">{bulkResult.summary.created}</p>
                    <p className="text-[10px] text-green-600 font-medium">Created</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-black text-amber-600">{bulkResult.summary.skipped}</p>
                    <p className="text-[10px] text-amber-600 font-medium">Skipped</p>
                  </div>
                  <div className="bg-red-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-black text-red-600">{bulkResult.summary.errors}</p>
                    <p className="text-[10px] text-red-600 font-medium">Errors</p>
                  </div>
                </div>

                {bulkResult.skipped?.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-amber-600 mb-1">Skipped (duplicates):</p>
                    <div className="max-h-24 overflow-y-auto text-xs text-gray-500 space-y-0.5">
                      {bulkResult.skipped.map((s, i) => (
                        <p key={i}>Row {s.row}: {s.email} — {s.reason}</p>
                      ))}
                    </div>
                  </div>
                )}

                {bulkResult.errors?.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-red-600 mb-1">Errors:</p>
                    <div className="max-h-24 overflow-y-auto text-xs text-gray-500 space-y-0.5">
                      {bulkResult.errors.map((e, i) => (
                        <p key={i}>Row {e.row}: {e.email} — {e.reason}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}

      <div className="space-y-3">
        {filtered.map(u => {
          const isUserSuperAdmin = (u.roles || []).includes('super_admin');
          return (
          <div key={u.id} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <p className="font-extrabold text-sm">{u.name}</p>
                <p className="text-xs text-gray-400">{u.email}</p>
                <p className="text-xs text-gray-300 mt-0.5">Joined {fmt(u.created_at)}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setEditUser(u); setEditForm({ name: u.name, email: u.email }); }}
                  className="text-xs font-bold text-[#2B3A67] border border-[#2B3A67] px-2 py-1 rounded-lg"
                  data-testid={`edit-user-${u.id}`}>
                  Edit
                </button>
                {isSuperAdmin && (
                  <button onClick={() => { setResetPwUser(u); setNewPassword(''); }}
                    className="text-xs font-bold text-amber-600 border border-amber-400 px-2 py-1 rounded-lg hover:bg-amber-50"
                    data-testid={`reset-pw-${u.id}`}>
                    Reset PW
                  </button>
                )}
                {isSuperAdmin && !isUserSuperAdmin && (
                  <button onClick={() => setDeletingUser(u)}
                    className="text-xs font-bold text-red-500 border border-red-300 px-2 py-1 rounded-lg hover:bg-red-50"
                    data-testid={`delete-user-${u.id}`}>
                    Delete
                  </button>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {(u.roles || []).map(r => <RoleBadge key={r} role={r} />)}
            </div>
            <div className="border-t border-gray-100 pt-3">
              <p className="text-xs text-gray-400 mb-2">Toggle Roles</p>
              <div className="flex flex-wrap gap-2">
                {TOGGLEABLE.map(role => {
                  const active = (u.roles || []).includes(role);
                  return (
                    <button key={role} onClick={() => toggleRole(u.id, role, !active)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-bold border transition-colors ${active ? 'bg-[#2B3A67] text-white border-[#2B3A67]' : 'bg-white text-gray-500 border-gray-200 hover:border-[#2B3A67]'}`}>
                      {active ? '✓ ' : '+ '}{ROLE_META[role]?.label || role}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )})}
      </div>
    </div>
  );
}
